// Shared client/staff document view.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { documentApi } from "../../backend/api/documents";
import { projectApi } from "../../backend/api/projects";
import type { DocumentRecord } from "../../backend/api/documents";
import { useCurrentUser } from "../../backend/queries/auth";
import { Download, FileText, Search, Trash2, Upload } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const bytes = (n = 0) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};
const fileBadge = (mime = "") => {
  if (mime.startsWith("image/")) return "Image";
  if (mime === "application/pdf") return "PDF";
  if (mime.startsWith("video/")) return "Video";
  if (mime.startsWith("audio/")) return "Audio";
  if (mime.includes("zip")) return "Archive";
  return "File";
};

export default function ClientDocument() {
  const [docs, setDocs] = useState<DocumentRecord[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [actionError, setActionError] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 8;

  const [targetProjectId, setTargetProjectId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { data: currentUser, isPending: authLoading } = useCurrentUser();
  const me = currentUser?.profile || currentUser || null;
  const location = useLocation();
  const forcedProjectId =
    location.state?.projectId != null ? String(location.state.projectId) : null;

  const isStaff = (role) =>
    ["agent", "admin"].includes((role || "").toLowerCase());
  useEffect(() => {
    if (!me?.id) return;
    let active = true;
    projectApi.list().then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setActionError(error.message || "Could not load projects.");
        setProjects([]);
        setTargetProjectId(null);
        return;
      }
      const accessible = data || [];
      setProjects(accessible);
      setTargetProjectId((current) => {
        if (current && accessible.some((project) => String(project.id) === current)) {
          return current;
        }
        if (
          forcedProjectId &&
          accessible.some((project) => String(project.id) === forcedProjectId)
        ) {
          return forcedProjectId;
        }
        return accessible[0]?.id != null ? String(accessible[0].id) : null;
      });
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  // Fetch docs
  const fetchDocs = async () => {
    if (!me?.id || !targetProjectId) {
      setDocs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setActionError("");
    const { data, error } = await documentApi.list({
      project_id: targetProjectId,
    });
    setLoading(false);
    if (error) {
      console.error("load docs", error);
      setActionError(error.message || "Could not load documents.");
      setDocs([]);
    } else {
      setDocs(data || []);
    }
  };

  useEffect(() => {
    if (!me?.id) return;
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id, targetProjectId]);

  // Filter + paginate
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        d.mime_type?.toLowerCase().includes(q),
    );
  }, [docs, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const clampedPage = Math.min(page, totalPages);
  const start = (clampedPage - 1) * pageSize;
  const pageItems = filtered.slice(start, start + pageSize);

  // Actions
  const handleDownload = async (doc: DocumentRecord) => {
    setActionError("");
    const { data, error } = await documentApi.download(doc.id);
    if (error || !data) {
      setActionError(error?.message || "Could not download this document.");
      return;
    }
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!me?.id) return;
    if (!targetProjectId) return;

    setBusy(true);
    setActionError("");
    const { error } = await documentApi.upload(targetProjectId, file, true);
    setBusy(false);
    if (error) {
      setActionError(error.message || "Could not upload this document.");
      return;
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
    fetchDocs();
  };

  const handleDelete = async (doc: DocumentRecord) => {
    if (!window.confirm(`Delete ${doc.name}?`)) return;
    setBusy(true);
    setActionError("");
    const { error } = await documentApi.delete(doc.id);
    setBusy(false);
    if (error) {
      setActionError(error.message || "Could not delete this document.");
      return;
    }
    await fetchDocs();
  };

  const canManage = isStaff(me?.role);

  return (
    <div className="flex flex-col">

      <div className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold">Documents</h1>
              <p className="text-sm text-muted-foreground">
                Upload and view documents for your projects.
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-[minmax(220px,1fr)_auto] lg:flex lg:items-center">
              <Select
                value={targetProjectId || undefined}
                onValueChange={(value) => {
                  setTargetProjectId(value || null);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-full lg:w-[340px]">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => {
                    const clientName = project.customer
                      ? `${project.customer.firstname || ""} ${project.customer.lastname || ""}`.trim()
                      : "Client";
                    return (
                      <SelectItem key={project.id} value={String(project.id)}>
                        #{project.id} · {clientName} ·{" "}
                        {project.project_type || "Relocation"}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  className="!pl-9"
                  placeholder="Search files..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              {me?.id && (
                <>
                  <Button
                    type="button"
                    disabled={busy || !targetProjectId}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4" />
                    {busy ? "Uploading..." : "Upload"}
                  </Button>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                    className="hidden"
                    aria-label="Upload project document"
                    onChange={handleUpload}
                    ref={fileInputRef}
                    disabled={busy || !targetProjectId}
                  />
                </>
              )}
            </div>
          </div>

          {actionError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {actionError}
            </div>
          )}

          <Card>
            <CardHeader className="p-4 sm:p-5">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Files
              </CardTitle>
              <CardDescription>
                {filtered.length} documents shown
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
              <div className="space-y-3 md:hidden">
                {loading || authLoading ? (
                  <EmptyDocuments text="Loading documents..." />
                ) : pageItems.length === 0 ? (
                  <EmptyDocuments text="No documents found." />
                ) : (
                  pageItems.map((doc) => (
                    <div key={doc.id} className="rounded-md border bg-card p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold">
                            {doc.name || "(untitled)"}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {bytes(Number(doc.size_bytes || 0))}
                          </div>
                        </div>
                        <Badge variant="outline">
                          {doc.storage_status === "available"
                            ? fileBadge(doc.mime_type)
                            : "Unavailable"}
                        </Badge>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={doc.storage_status !== "available"}
                          onClick={() => handleDownload(doc)}
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </Button>
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={busy}
                            aria-label={`Delete ${doc.name}`}
                            onClick={() => handleDelete(doc)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="hidden overflow-x-auto rounded-md border md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading || authLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="h-24 text-center text-muted-foreground"
                        >
                          Loading documents...
                        </TableCell>
                      </TableRow>
                    ) : pageItems.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="h-24 text-center text-muted-foreground"
                        >
                          No documents found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      pageItems.map((doc) => (
                        <TableRow key={doc.id}>
                          <TableCell className="font-medium">
                            {doc.name || "(untitled)"}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {doc.storage_status === "available"
                                ? fileBadge(doc.mime_type)
                                : "Unavailable"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {bytes(Number(doc.size_bytes || 0))}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Download document"
                                disabled={doc.storage_status !== "available"}
                                onClick={() => handleDownload(doc)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              {canManage && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={busy}
                                  aria-label={`Delete ${doc.name}`}
                                  onClick={() => handleDelete(doc)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={clampedPage === 1}
                >
                  Previous
                </Button>

                <span className="text-sm text-muted-foreground">
                  Page {clampedPage} of {totalPages}
                </span>

                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={clampedPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

const EmptyDocuments = ({ text }) => (
  <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
    {text}
  </div>
);
